import { useCallback, useEffect, useState } from "react";
import { adminGetEmailSettings, adminUpdateEmailSettings, adminTestEmail, adminNewsletterSubscribers, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Envelope, PaperPlaneTilt, ArrowsClockwise } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";
import AdminFormInput from "../components/AdminFormInput";
import AdminFormSelect from "../components/AdminFormSelect";
import AdminPrimaryButton from "../components/AdminPrimaryButton";
import AdminToggle from "../components/AdminToggle";

export default function EmailConfigTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmailVal, setTestEmailVal] = useState("");
  const [err, setErr] = useState("");
  const [subTab, setSubTab] = useState("provider");

  const [providerType, setProviderType] = useState("resend");
  const [apiKey, setApiKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("587");
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPass, setSmtpPass] = useState("");
  const [useSsl, setUseSsl] = useState(true);
  const [isActive, setIsActive] = useState(false);

  const [subscribers, setSubscribers] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    adminGetEmailSettings()
      .then((data) => {
        if (!mounted) return;
        setConfig(data);
        if (data && Object.keys(data).length > 0) {
          setProviderType(data.provider_type || "resend");
          setSenderEmail(data.sender_email || "");
          setSenderName(data.sender_name || "");
          setReplyTo(data.reply_to_email || "");
          setSmtpHost(data.smtp_host || "");
          setSmtpPort(String(data.smtp_port || 587));
          setSmtpUser(data.smtp_username || "");
          setUseSsl(data.use_ssl !== false);
          setIsActive(data.is_active === true);
        }
      })
      .catch((e) => { if (mounted) setErr(formatApiError(e)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const loadSubscribers = useCallback(() => {
    setSubsLoading(true);
    let mounted = true;
    adminNewsletterSubscribers()
      .then((d) => { if (mounted) setSubscribers(d); })
      .catch((e) => { if (mounted) toast.error(formatApiError(e)); })
      .finally(() => { if (mounted) setSubsLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (subTab === "subscribers" && !subscribers) {
      const cleanup = loadSubscribers();
      return cleanup;
    }
  }, [subTab, subscribers, loadSubscribers]);

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    const payload = {
      provider_type: providerType,
      sender_email: senderEmail,
      sender_name: senderName,
      reply_to_email: replyTo || undefined,
      use_ssl: useSsl,
      is_active: isActive,
    };
    if (apiKey) payload.api_key = apiKey;
    if (smtpHost) payload.smtp_host = smtpHost;
    if (smtpPort) payload.smtp_port = parseInt(smtpPort, 10);
    if (smtpUser) payload.smtp_username = smtpUser;
    if (smtpPass) payload.smtp_password = smtpPass;
    try {
      const updated = await adminUpdateEmailSettings(payload);
      setConfig(updated);
      setApiKey(""); setSmtpPass("");
      toast.success("Email configuration saved");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!testEmailVal) { toast.error("Enter a recipient email for the test"); return; }
    setTesting(true);
    try {
      await adminTestEmail(testEmailVal);
      toast.success(`Test email queued for ${testEmailVal}`);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p className="text-slate-400 text-sm">Loading…</p>;

  const inputCls = "w-full bg-white border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none rounded-lg px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400";

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Envelope size={18} className="text-indigo-600" weight="fill" />
        <h2 className="text-2xl font-bold text-slate-900">Email Configuration</h2>
        <AdminBadge variant={config?.is_active ? "success" : "idle"}>
          {config?.is_active ? "Active" : "Inactive"}
        </AdminBadge>
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b border-slate-200 mb-6">
        {[
          { id: "provider", label: "Provider Settings" },
          { id: "subscribers", label: "Newsletter Subscribers" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              subTab === t.id
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "provider" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Config form */}
          <form onSubmit={onSave} className="space-y-5">
            {/* Active toggle */}
            <div className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl">
              <div>
                <p className="text-sm font-semibold text-slate-900">Enable Email System</p>
                <p className="text-xs text-slate-500 mt-0.5">Toggle email sending on/off globally</p>
              </div>
              <AdminToggle enabled={isActive} onToggle={() => setIsActive((v) => !v)} size={32} />
            </div>

            <AdminFormSelect
              label="Email Provider"
              value={providerType}
              onChange={(e) => setProviderType(e.target.value)}
            >
              <option value="resend">Resend</option>
              <option value="smtp">SMTP</option>
            </AdminFormSelect>

            {providerType === "resend" && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                  Resend API Key
                  {config?.api_key_masked && (
                    <span className="ml-2 text-slate-400 normal-case tracking-normal font-normal">
                      (current: {config.api_key_masked})
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="re_xxxxxxxxxxxx"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="mt-1 text-xs text-slate-400">Leave blank to keep existing key.</p>
              </div>
            )}

            {providerType === "smtp" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <AdminFormInput label="SMTP Host" type="text" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} placeholder="smtp.gmail.com" />
                  <AdminFormInput label="Port" type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} placeholder="587" />
                </div>
                <AdminFormInput label="SMTP Username" type="text" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} placeholder="user@gmail.com" autoComplete="off" />
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                    SMTP Password
                    {config?.smtp_password_masked && (
                      <span className="ml-2 text-slate-400 normal-case tracking-normal font-normal">
                        (current: {config.smtp_password_masked})
                      </span>
                    )}
                  </label>
                  <input type="password" value={smtpPass} onChange={(e) => setSmtpPass(e.target.value)} placeholder="••••••••" className={inputCls} autoComplete="new-password" />
                  <p className="mt-1 text-xs text-slate-400">Leave blank to keep existing password.</p>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <span className="text-sm text-slate-600">Use SSL/TLS</span>
                  <AdminToggle enabled={useSsl} onToggle={() => setUseSsl((v) => !v)} size={24} />
                </div>
              </div>
            )}

            <div className="space-y-3 pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sender Identity</p>
              <div className="grid grid-cols-2 gap-3">
                <AdminFormInput label="Sender Name" type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Trend Ming" />
                <AdminFormInput label="Sender Email" type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} placeholder="news@trendming.com" />
              </div>
              <AdminFormInput label="Reply-To Email (optional)" type="email" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="support@trendming.com" />
            </div>

            {err && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>}

            <AdminPrimaryButton type="submit" busy={saving} busyLabel="Saving…" className="w-full justify-center">
              Save Configuration
            </AdminPrimaryButton>
          </form>

          {/* Right panel */}
          <div className="space-y-6">
            {config && Object.keys(config).length > 0 && (
              <AdminCard header="Current Configuration">
                <div className="p-5 space-y-3 text-sm">
                  {[
                    ["Provider", <span className="font-mono uppercase">{config.provider_type || "—"}</span>],
                    ["Sender", `${config.sender_name || "—"} <${config.sender_email || "—"}>`],
                    config.api_key_masked && ["API Key", <span className="font-mono text-slate-500">{config.api_key_masked}</span>],
                    config.smtp_host && ["SMTP Host", `${config.smtp_host}:${config.smtp_port}`],
                    ["Status", <AdminBadge variant={config.is_active ? "success" : "idle"}>{config.is_active ? "Active" : "Inactive"}</AdminBadge>],
                  ].filter(Boolean).map(([label, val]) => (
                    <div key={label} className="flex justify-between gap-4">
                      <span className="text-slate-500 shrink-0">{label}</span>
                      <span className="text-slate-900 text-right">{val}</span>
                    </div>
                  ))}
                </div>
              </AdminCard>
            )}

            <AdminCard>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <PaperPlaneTilt size={14} className="text-indigo-600" weight="fill" />
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Send Test Email</p>
                </div>
                <p className="text-xs text-slate-500 mb-3">Verify your configuration is working by sending a test email.</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={testEmailVal}
                    onChange={(e) => setTestEmailVal(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 bg-white border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none rounded-lg px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={onTest}
                    disabled={testing || !testEmailVal}
                    className="bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 font-semibold px-4 py-2.5 text-sm rounded-lg transition-colors whitespace-nowrap"
                  >
                    {testing ? "Sending…" : "Send Test"}
                  </button>
                </div>
              </div>
            </AdminCard>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">Security</p>
              <ul className="text-xs text-amber-700 space-y-1">
                <li>• API keys and passwords are encrypted at rest</li>
                <li>• Secrets are never logged or exposed in errors</li>
                <li>• Masked values shown in UI — write-only update</li>
                <li>• Only Super Admins can modify this configuration</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {subTab === "subscribers" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">
              {subscribers ? `${subscribers.total} total subscribers` : "Loading…"}
            </p>
            <button
              onClick={loadSubscribers}
              disabled={subsLoading}
              className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 transition-colors"
            >
              <ArrowsClockwise size={12} className={subsLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
          {subsLoading && <p className="text-slate-400 text-sm">Loading…</p>}
          {subscribers?.subscribers.length === 0 && <p className="text-slate-400 text-sm">No subscribers yet.</p>}
          {subscribers?.subscribers.length > 0 && (
            <AdminCard>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      {["Email", "Verified", "Source", "Subscribed", "Unsubscribed"].map((h) => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.subscribers.map((s, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.email}</td>
                        <td className="px-4 py-3">
                          <AdminBadge variant={s.is_verified ? "success" : "idle"}>
                            {s.is_verified ? "Yes" : "Pending"}
                          </AdminBadge>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{s.source || "—"}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{s.unsubscribed_at ? new Date(s.unsubscribed_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AdminCard>
          )}
        </div>
      )}
    </div>
  );
}
